import {
  Box,
  CircularProgress,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'

import useListLocationDetails from '../../dataFetchHooks/useListLocationDetails'
import useListLocationMetricValues from '../../dataFetchHooks/useListLocationMetricValues'
import { IAiBlackboxProps, IMetricValue } from '../../type'
import LocationGroupTable from './LocationGroupTable'
import LocationTable from './LocationTable'

type Order = 'asc' | 'desc'
type OrderBy =
  | 'location'
  | 'sales_forecast'
  | 'sales_actual'
  | 'sales_variance'
  | 'spmh_daily_average'
  | 'labor_scheduled_hours'
  | 'labor_actual_hours'
  | 'labor_variance_hours'
  | 'ot_scheduled_hours'
  | 'ot_actual_hours'
  | 'labor_scheduled_percent'
  | 'labor_actual_percent'
  | 'labor_variance_percent'

interface LocationMetricRow {
  locationId: number
  locationCode: string
  locationName: string
  salesForecast: IMetricValue | null
  salesActual: IMetricValue | null
  salesVariance: IMetricValue | null
  spmhDailyAverage: IMetricValue | null
  laborScheduledHours: IMetricValue | null
  laborActualHours: IMetricValue | null
  laborVarianceHours: IMetricValue | null
  otScheduledHours: IMetricValue | null
  otActualHours: IMetricValue | null
  laborScheduledPercent: IMetricValue | null
  laborActualPercent: IMetricValue | null
  laborVariancePercent: IMetricValue | null
}

// Define metric codes as a constant outside the component
const METRIC_CODES = [
  'sales_forecast',
  'sales_actual',
  'sales_variance',
  'spmh_daily_average',
  'labor_scheduled_hours',
  'labor_actual_hours',
  'labor_variance_hours',
  'ot_scheduled_hours',
  'ot_actual_hours',
  'labor_scheduled_percent',
  'labor_actual_percent',
  'labor_variance_percent',
]

const FwOpsReportHalfRebuild: React.FC<IAiBlackboxProps> = ({
  startDate = '',
  endDate = '',
  queryFilter = { isBreakdownByLocationGroup: false, locationIds: [] },
}) => {
  const [order, setOrder] = useState<Order>('asc')
  const [orderBy, setOrderBy] = useState<OrderBy>('location')
  const [page, setPage] = useState(1)
  const rowsPerPage = 20

  // Fetch location details
  const { data: locationsMap, isLoading: isLoadingLocations } =
    useListLocationDetails()

  // Fetch metric values
  const { data: metricsData, isLoading: isLoadingMetrics } =
    useListLocationMetricValues(
      startDate,
      endDate,
      queryFilter || { isBreakdownByLocationGroup: false, locationIds: [] },
      METRIC_CODES,
    )

  // Reset pagination when data changes
  useEffect(() => {
    setPage(1)
  }, [startDate, endDate, queryFilter])

  // Prepare table data
  const tableData = useMemo(() => {
    if (!metricsData?.locations || !locationsMap) {
      return []
    }

    return metricsData.locations
      .map((location) => {
        const locationDetail = locationsMap[location.locationId] || {
          code: '',
          name: '',
        }

        // Get the metric values
        const laborScheduledPercent =
          location.metricData?.labor_scheduled_percent || null
        const laborActualPercent =
          location.metricData?.labor_actual_percent || null
        const laborVariancePercent =
          location.metricData?.labor_variance_percent || null

        // Convert percent values to percentage by multiplying by 100
        const convertedLaborScheduledPercent = laborScheduledPercent
          ? {
              ...laborScheduledPercent,
              value: laborScheduledPercent.value * 100,
            }
          : null
        const convertedLaborActualPercent = laborActualPercent
          ? { ...laborActualPercent, value: laborActualPercent.value * 100 }
          : null
        const convertedLaborVariancePercent = laborVariancePercent
          ? { ...laborVariancePercent, value: laborVariancePercent.value * 100 }
          : null

        return {
          locationId: location.locationId,
          locationCode: locationDetail?.code || '',
          locationName: locationDetail?.name || '',
          salesForecast: location.metricData?.sales_forecast || null,
          salesActual: location.metricData?.sales_actual || null,
          salesVariance: location.metricData?.sales_variance || null,
          spmhDailyAverage: location.metricData?.spmh_daily_average || null,
          laborScheduledHours:
            location.metricData?.labor_scheduled_hours || null,
          laborActualHours: location.metricData?.labor_actual_hours || null,
          laborVarianceHours: location.metricData?.labor_variance_hours || null,
          otScheduledHours: location.metricData?.ot_scheduled_hours || null,
          otActualHours: location.metricData?.ot_actual_hours || null,
          laborScheduledPercent: convertedLaborScheduledPercent,
          laborActualPercent: convertedLaborActualPercent,
          laborVariancePercent: convertedLaborVariancePercent,
        }
      })
      .filter(
        (row) =>
          // Filter out rows where all metric values are null or undefined
          row.salesForecast !== null ||
          row.salesActual !== null ||
          row.salesVariance !== null ||
          row.spmhDailyAverage !== null ||
          row.laborScheduledHours !== null ||
          row.laborActualHours !== null ||
          row.laborVarianceHours !== null ||
          row.otScheduledHours !== null ||
          row.otActualHours !== null ||
          row.laborScheduledPercent !== null ||
          row.laborActualPercent !== null ||
          row.laborVariancePercent !== null,
      )
  }, [metricsData, locationsMap])

  // Calculate totals
  const totals = useMemo(() => {
    // If no data is available, return zeros
    if (!metricsData?.summary) {
      return {
        salesForecastTotal: 0,
        salesActualTotal: 0,
        salesVarianceTotal: 0,
        spmhDailyAverageTotal: 0,
        laborScheduledHoursTotal: 0,
        laborActualHoursTotal: 0,
        laborVarianceHoursTotal: 0,
        otScheduledHoursTotal: 0,
        otActualHoursTotal: 0,
        laborScheduledPercentTotal: 0,
        laborActualPercentTotal: 0,
        laborVariancePercentTotal: 0,
      }
    }

    // Extract values from summary data
    const summary = metricsData.summary

    // Get values from summary or default to 0
    const salesForecastTotal = summary.sales_forecast?.value ?? 0
    const salesActualTotal = summary.sales_actual?.value ?? 0
    const salesVarianceTotal = summary.sales_variance?.value ?? 0

    // Calculate average of non-zero spmhDailyAverage values
    let spmhDailyAverageTotal = 0
    if (tableData.length > 0) {
      const nonZeroSpmhValues = tableData
        .filter(
          (row) =>
            row.spmhDailyAverage?.value && row.spmhDailyAverage.value !== 0,
        )
        .map((row) => row.spmhDailyAverage!.value)

      if (nonZeroSpmhValues.length > 0) {
        const sum = nonZeroSpmhValues.reduce((acc, val) => acc + val, 0)
        spmhDailyAverageTotal = sum / nonZeroSpmhValues.length
      }
    }

    const laborScheduledHoursTotal = summary.labor_scheduled_hours?.value ?? 0
    const laborActualHoursTotal = summary.labor_actual_hours?.value ?? 0
    const laborVarianceHoursTotal = summary.labor_variance_hours?.value ?? 0
    const otScheduledHoursTotal = summary.ot_scheduled_hours?.value ?? 0
    const otActualHoursTotal = summary.ot_actual_hours?.value ?? 0

    // For percentage metrics, convert to percentage by multiplying by 100
    const laborScheduledPercentTotal = summary.labor_scheduled_percent?.value
      ? summary.labor_scheduled_percent.value * 100
      : 0
    const laborActualPercentTotal = summary.labor_actual_percent?.value
      ? summary.labor_actual_percent.value * 100
      : 0
    const laborVariancePercentTotal = summary.labor_variance_percent?.value
      ? summary.labor_variance_percent.value * 100
      : 0

    return {
      salesForecastTotal,
      salesActualTotal,
      salesVarianceTotal,
      spmhDailyAverageTotal,
      laborScheduledHoursTotal,
      laborActualHoursTotal,
      laborVarianceHoursTotal,
      otScheduledHoursTotal,
      otActualHoursTotal,
      laborScheduledPercentTotal,
      laborActualPercentTotal,
      laborVariancePercentTotal,
    }
  }, [metricsData, tableData])

  // Handle sorting
  const handleRequestSort = (property: OrderBy) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  const sortedData = useMemo(() => {
    if (tableData.length === 0) {
      return []
    }

    return [...tableData].sort((a, b) => {
      let comparison = 0

      if (orderBy === 'location') {
        const aLocation = `${a.locationCode} - ${a.locationName}`
        const bLocation = `${b.locationCode} - ${b.locationName}`
        comparison = aLocation.localeCompare(bLocation)
      } else {
        // Get the property name based on orderBy
        const propertyMap: Record<OrderBy, keyof LocationMetricRow> = {
          location: 'locationName',
          sales_forecast: 'salesForecast',
          sales_actual: 'salesActual',
          sales_variance: 'salesVariance',
          spmh_daily_average: 'spmhDailyAverage',
          labor_scheduled_hours: 'laborScheduledHours',
          labor_actual_hours: 'laborActualHours',
          labor_variance_hours: 'laborVarianceHours',
          ot_scheduled_hours: 'otScheduledHours',
          ot_actual_hours: 'otActualHours',
          labor_scheduled_percent: 'laborScheduledPercent',
          labor_actual_percent: 'laborActualPercent',
          labor_variance_percent: 'laborVariancePercent',
        }

        const property = propertyMap[orderBy]
        // Fix type errors by ensuring we're working with IMetricValue objects
        const aValue =
          typeof a[property] === 'object' && a[property] !== null
            ? (a[property] as IMetricValue).value ?? 0
            : 0
        const bValue =
          typeof b[property] === 'object' && b[property] !== null
            ? (b[property] as IMetricValue).value ?? 0
            : 0
        comparison = aValue - bValue
      }

      return order === 'asc' ? comparison : -comparison
    })
  }, [tableData, order, orderBy])

  // Handle pagination
  const handleChangePage = (
    event: React.ChangeEvent<unknown>,
    newPage: number,
  ) => {
    setPage(newPage)
  }

  const paginatedData = useMemo(() => {
    const startIndex = (page - 1) * rowsPerPage
    const endIndex = startIndex + rowsPerPage
    return sortedData.slice(startIndex, endIndex)
  }, [sortedData, page, rowsPerPage])

  // Format number with commas for thousands
  const formatNumber = (
    value: number | undefined,
    isCurrency: boolean = false,
    isPercentage: boolean = false,
    decimals?: number,
  ) => {
    if (value === undefined || value === null) return '-'

    // Determine decimal places based on the PRD requirements
    let decimalPlaces = decimals
    if (isCurrency && decimalPlaces === undefined) {
      decimalPlaces = Math.abs(value) < 1000 ? 2 : 0
    } else if (isPercentage && decimalPlaces === undefined) {
      decimalPlaces = 2
    }

    const formattedNumber = value.toLocaleString('en-US', {
      minimumFractionDigits: decimalPlaces ?? 2,
      maximumFractionDigits: decimalPlaces ?? 2,
    })

    if (isCurrency) {
      return `$${formattedNumber}`
    } else if (isPercentage) {
      return `${formattedNumber}%`
    }
    return formattedNumber
  }

  // Add theme and media query for responsive design
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  // Add default locationGroupIds if it's a location group breakdown
  const enhancedQueryFilter = queryFilter
    ? queryFilter.isBreakdownByLocationGroup
      ? {
          isBreakdownByLocationGroup: true,
          locationGroupIds: queryFilter.locationGroupIds || [],
          ...(queryFilter.intersectedLocationGroupIds?.length
            ? {
                intersectedLocationGroupIds:
                  queryFilter.intersectedLocationGroupIds,
              }
            : {}),
        }
      : {
          isBreakdownByLocationGroup: false,
          locationIds: queryFilter.locationIds || [],
          ...(queryFilter.intersectedLocationGroupIds?.length
            ? {
                intersectedLocationGroupIds:
                  queryFilter.intersectedLocationGroupIds,
              }
            : {}),
        }
    : {
        isBreakdownByLocationGroup: false,
        locationIds: [],
      }

  if (isLoadingLocations || isLoadingMetrics) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="200px"
      >
        <CircularProgress />
      </Box>
    )
  }

  if (!metricsData || !locationsMap || tableData.length === 0) {
    return (
      <Box p={2}>
        <Typography variant="body1">
          No data available for the selected filters.
        </Typography>
      </Box>
    )
  }

  return enhancedQueryFilter.isBreakdownByLocationGroup ? (
    <LocationGroupTable
      startDate={startDate}
      endDate={endDate}
      queryFilter={enhancedQueryFilter}
    />
  ) : (
    <Box
      p={isMobile ? 1 : 1.5}
      sx={{
        fontFamily: 'Lexend-Regular',
        width: '100%',
        overflowX: 'auto',
      }}
    >
      <Box
        sx={{
          maxWidth: '100%',
          margin: '0 auto',
          overflowX: 'auto',
        }}
      >
        <TableContainer
          sx={{
            width: '100%',
            overflowX: 'auto',
            '&::-webkit-scrollbar': {
              height: '8px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.2)',
              borderRadius: '4px',
            },
          }}
        >
          <Table
            size="small"
            sx={{
              fontSize: '0.875rem',
              tableLayout: 'fixed',
              minWidth: '1200px',
              '& .MuiTableCell-root': {
                padding: isMobile ? '6px 8px' : '8px 12px',
              },
              '& .MuiTableHead-root': {
                backgroundColor: '#f5f5f5',
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    width: '200px',
                    whiteSpace: 'normal',
                    minWidth: '120px',
                    height: '60px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  <TableSortLabel
                    active={orderBy === 'location'}
                    direction={orderBy === 'location' ? order : 'asc'}
                    onClick={() => handleRequestSort('location')}
                  >
                    Store
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    whiteSpace: 'normal',
                    minWidth: '100px',
                    height: '60px',
                  }}
                >
                  <TableSortLabel
                    active={orderBy === 'sales_forecast'}
                    direction={orderBy === 'sales_forecast' ? order : 'asc'}
                    onClick={() => handleRequestSort('sales_forecast')}
                  >
                    Sales Forecast
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    whiteSpace: 'normal',
                    minWidth: '100px',
                    height: '60px',
                  }}
                >
                  <TableSortLabel
                    active={orderBy === 'sales_actual'}
                    direction={orderBy === 'sales_actual' ? order : 'asc'}
                    onClick={() => handleRequestSort('sales_actual')}
                  >
                    Sales Actual
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    whiteSpace: 'normal',
                    minWidth: '100px',
                    height: '60px',
                  }}
                >
                  <TableSortLabel
                    active={orderBy === 'sales_variance'}
                    direction={orderBy === 'sales_variance' ? order : 'asc'}
                    onClick={() => handleRequestSort('sales_variance')}
                  >
                    Sales Variance
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    whiteSpace: 'normal',
                    minWidth: '100px',
                    height: '60px',
                  }}
                >
                  <TableSortLabel
                    active={orderBy === 'spmh_daily_average'}
                    direction={orderBy === 'spmh_daily_average' ? order : 'asc'}
                    onClick={() => handleRequestSort('spmh_daily_average')}
                  >
                    SPMH by Date
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    whiteSpace: 'normal',
                    minWidth: '100px',
                    height: '60px',
                  }}
                >
                  <TableSortLabel
                    active={orderBy === 'labor_scheduled_hours'}
                    direction={
                      orderBy === 'labor_scheduled_hours' ? order : 'asc'
                    }
                    onClick={() => handleRequestSort('labor_scheduled_hours')}
                  >
                    Labor Scheduled Hours
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    whiteSpace: 'normal',
                    minWidth: '100px',
                    height: '60px',
                  }}
                >
                  <TableSortLabel
                    active={orderBy === 'labor_actual_hours'}
                    direction={orderBy === 'labor_actual_hours' ? order : 'asc'}
                    onClick={() => handleRequestSort('labor_actual_hours')}
                  >
                    Labor Actual Hours
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    whiteSpace: 'normal',
                    minWidth: '100px',
                    height: '60px',
                  }}
                >
                  <TableSortLabel
                    active={orderBy === 'labor_variance_hours'}
                    direction={
                      orderBy === 'labor_variance_hours' ? order : 'asc'
                    }
                    onClick={() => handleRequestSort('labor_variance_hours')}
                  >
                    Labor Variance Hours
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    whiteSpace: 'normal',
                    minWidth: '100px',
                    height: '60px',
                  }}
                >
                  <TableSortLabel
                    active={orderBy === 'ot_scheduled_hours'}
                    direction={orderBy === 'ot_scheduled_hours' ? order : 'asc'}
                    onClick={() => handleRequestSort('ot_scheduled_hours')}
                  >
                    OT Scheduled Hours
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    whiteSpace: 'normal',
                    minWidth: '100px',
                    height: '60px',
                  }}
                >
                  <TableSortLabel
                    active={orderBy === 'ot_actual_hours'}
                    direction={orderBy === 'ot_actual_hours' ? order : 'asc'}
                    onClick={() => handleRequestSort('ot_actual_hours')}
                  >
                    OT Actual Hours
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    whiteSpace: 'normal',
                    minWidth: '100px',
                    height: '60px',
                  }}
                >
                  <TableSortLabel
                    active={orderBy === 'labor_scheduled_percent'}
                    direction={
                      orderBy === 'labor_scheduled_percent' ? order : 'asc'
                    }
                    onClick={() => handleRequestSort('labor_scheduled_percent')}
                  >
                    Labor Scheduled Percent
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    whiteSpace: 'normal',
                    minWidth: '100px',
                    height: '60px',
                  }}
                >
                  <TableSortLabel
                    active={orderBy === 'labor_actual_percent'}
                    direction={
                      orderBy === 'labor_actual_percent' ? order : 'asc'
                    }
                    onClick={() => handleRequestSort('labor_actual_percent')}
                  >
                    Labor Actual Percent
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    whiteSpace: 'normal',
                    minWidth: '100px',
                    height: '60px',
                  }}
                >
                  <TableSortLabel
                    active={orderBy === 'labor_variance_percent'}
                    direction={
                      orderBy === 'labor_variance_percent' ? order : 'asc'
                    }
                    onClick={() => handleRequestSort('labor_variance_percent')}
                  >
                    Labor Variance Percent
                  </TableSortLabel>
                </TableCell>
              </TableRow>
              {/* Total row just below the header */}
              <TableRow sx={{ '& > *': { fontFamily: 'Lexend-SemiBold' } }}>
                <TableCell
                  component="th"
                  scope="row"
                  sx={{
                    whiteSpace: 'nowrap',
                  }}
                >
                  Total
                </TableCell>
                <TableCell align="right">
                  {formatNumber(totals.salesForecastTotal, true)}
                </TableCell>
                <TableCell align="right">
                  {formatNumber(totals.salesActualTotal, true)}
                </TableCell>
                <TableCell align="right">
                  {formatNumber(totals.salesVarianceTotal, true)}
                </TableCell>
                <TableCell align="right">
                  {formatNumber(totals.spmhDailyAverageTotal, true)}
                </TableCell>
                <TableCell align="right">
                  {formatNumber(totals.laborScheduledHoursTotal, false)}
                </TableCell>
                <TableCell align="right">
                  {formatNumber(totals.laborActualHoursTotal, false)}
                </TableCell>
                <TableCell align="right">
                  {formatNumber(totals.laborVarianceHoursTotal, false)}
                </TableCell>
                <TableCell align="right">
                  {formatNumber(totals.otScheduledHoursTotal, false)}
                </TableCell>
                <TableCell align="right">
                  {formatNumber(totals.otActualHoursTotal, false)}
                </TableCell>
                <TableCell align="right">
                  {formatNumber(totals.laborScheduledPercentTotal, false, true)}
                </TableCell>
                <TableCell align="right">
                  {formatNumber(totals.laborActualPercentTotal, false, true)}
                </TableCell>
                <TableCell align="right">
                  {formatNumber(totals.laborVariancePercentTotal, false, true)}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.map((row) => (
                <TableRow key={row.locationId}>
                  <TableCell
                    sx={{
                      whiteSpace: 'normal',
                      minWidth: '120px',
                      height: '60px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {`${row.locationCode} - ${row.locationName}`}
                  </TableCell>
                  <TableCell align="right">
                    {row.salesForecast
                      ? formatNumber(row.salesForecast.value, true)
                      : '-'}
                  </TableCell>
                  <TableCell align="right">
                    {row.salesActual
                      ? formatNumber(row.salesActual.value, true)
                      : '-'}
                  </TableCell>
                  <TableCell align="right">
                    {row.salesVariance
                      ? formatNumber(row.salesVariance.value, true)
                      : '-'}
                  </TableCell>
                  <TableCell align="right">
                    {row.spmhDailyAverage
                      ? formatNumber(row.spmhDailyAverage.value, true)
                      : '-'}
                  </TableCell>
                  <TableCell align="right">
                    {row.laborScheduledHours
                      ? formatNumber(row.laborScheduledHours.value, false)
                      : '-'}
                  </TableCell>
                  <TableCell align="right">
                    {row.laborActualHours
                      ? formatNumber(row.laborActualHours.value, false)
                      : '-'}
                  </TableCell>
                  <TableCell align="right">
                    {row.laborVarianceHours
                      ? formatNumber(row.laborVarianceHours.value, false)
                      : '-'}
                  </TableCell>
                  <TableCell align="right">
                    {row.otScheduledHours
                      ? formatNumber(row.otScheduledHours.value, false)
                      : '-'}
                  </TableCell>
                  <TableCell align="right">
                    {row.otActualHours
                      ? formatNumber(row.otActualHours.value, false)
                      : '-'}
                  </TableCell>
                  <TableCell align="right">
                    {row.laborScheduledPercent
                      ? formatNumber(
                          row.laborScheduledPercent.value,
                          false,
                          true,
                        )
                      : '-'}
                  </TableCell>
                  <TableCell align="right">
                    {row.laborActualPercent
                      ? formatNumber(row.laborActualPercent.value, false, true)
                      : '-'}
                  </TableCell>
                  <TableCell align="right">
                    {row.laborVariancePercent
                      ? formatNumber(
                          row.laborVariancePercent.value,
                          false,
                          true,
                        )
                      : '-'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {sortedData.length > rowsPerPage && (
          <Box display="flex" justifyContent="center" mt={1}>
            <Pagination
              count={Math.ceil(sortedData.length / rowsPerPage)}
              page={page}
              onChange={handleChangePage}
              color="primary"
              size="small"
              siblingCount={isMobile ? 0 : 1}
            />
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default FwOpsReportHalfRebuild
